/**
 * Minimal client for the Ory Kratos browser login flow.
 *
 * Driving the flow ourselves lets a single email field serve both the password
 * and the passwordless code method. The bundled Ory UI renders one form per
 * method group and repeats the shared identifier field in each of them.
 */

import {
  isUserCancellation,
  serializeAssertion,
  toRequestOptions,
  triggerOptions,
} from "./webauthn";

const KRATOS =
  process.env.REACT_APP_KRATOS_PUBLIC_URL || "http://localhost:4433";

export interface UiText {
  id?: number;
  text: string;
  type: string;
}

export interface UiNode {
  group: string;
  attributes: {
    name?: string;
    value?: unknown;
    type?: string;
    required?: boolean;
    onclick?: string;
  };
  messages?: UiText[];
}

export interface LoginFlow {
  id: string;
  ui: {
    action: string;
    method: string;
    nodes: UiNode[];
    messages?: UiText[];
  };
}

export type SubmitResult =
  /** Kratos issued a session cookie; ask the backend who we are. */
  | { kind: "success" }
  /** A code was emailed; collect it and submit the second step. */
  | { kind: "codeSent"; flow: LoginFlow; message: string }
  /** Bad password, wrong code, unknown address, and similar. */
  | { kind: "error"; flow?: LoginFlow; message: string }
  /** The flow went stale and must be recreated. */
  | { kind: "expired" }
  /** A valid Kratos session already exists. */
  | { kind: "alreadySignedIn" };

const GENERIC_ERROR = "Sign in failed. Please check your details and try again.";

function nodeNamed(flow: LoginFlow, name: string): UiNode | undefined {
  return flow.ui.nodes.find((n) => n.attributes.name === name);
}

export function csrfToken(flow: LoginFlow): string {
  const value = nodeNamed(flow, "csrf_token")?.attributes.value;
  return typeof value === "string" ? value : "";
}

/** Kratos signals "code required" by adding a visible code input to the flow. */
function expectsCode(flow: LoginFlow): boolean {
  return flow.ui.nodes.some(
    (n) => n.group === "code" && n.attributes.name === "code"
  );
}

export function messagesOf(flow: LoginFlow, type: string): string[] {
  const collected = [...(flow.ui.messages || [])];
  for (const node of flow.ui.nodes) {
    collected.push(...(node.messages || []));
  }
  return collected.filter((m) => m.type === type).map((m) => m.text);
}

async function requestLoginFlow(refresh: boolean): Promise<Response> {
  const query = refresh ? "?refresh=true" : "";
  return fetch(`${KRATOS}/self-service/login/browser${query}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
}

export async function createLoginFlow(): Promise<LoginFlow> {
  let res = await requestLoginFlow(false);

  // A leftover Kratos session refuses a plain login flow. Asking Kratos to
  // refresh it lets the engineer sign in again instead of dead-ending on the
  // "valid session was detected" error with no way forward.
  if (!res.ok && (await isSessionAlreadyAvailable(res))) {
    res = await requestLoginFlow(true);
  }

  if (!res.ok) {
    throw new Error(`Could not start a login flow (HTTP ${res.status})`);
  }
  return (await res.json()) as LoginFlow;
}

async function isSessionAlreadyAvailable(res: Response): Promise<boolean> {
  const payload = (await res
    .clone()
    .json()
    .catch(() => null)) as { error?: { id?: string } } | null;
  return payload?.error?.id === "session_already_available";
}

interface SubmitBody {
  method: "password" | "code";
  identifier: string;
  password?: string;
  code?: string;
  resend?: string;
}

async function submit(flow: LoginFlow, body: SubmitBody): Promise<SubmitResult> {
  const res = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...body, csrf_token: csrfToken(flow) }),
  });

  if (res.ok) return { kind: "success" };

  const payload = (await res.json().catch(() => null)) as
    | (Partial<LoginFlow> & { error?: { id?: string; message?: string } })
    | null;

  if (isRedirectAfterSuccess(res, payload)) return { kind: "success" };

  const errorId = payload?.error?.id;
  if (errorId === "session_already_available") return { kind: "alreadySignedIn" };
  if (
    res.status === 410 ||
    errorId === "self_service_flow_expired" ||
    errorId === "security_identity_mismatch"
  ) {
    return { kind: "expired" };
  }

  if (!payload?.ui) {
    return { kind: "error", message: payload?.error?.message || GENERIC_ERROR };
  }

  const updated = payload as LoginFlow;

  // Requesting a code answers 400 with the code input attached, which is a
  // continuation rather than a failure. Sending a code back that Kratos
  // rejects also answers 400, so only treat this as progress when we had no
  // code to submit yet.
  if (!body.code && expectsCode(updated)) {
    return {
      kind: "codeSent",
      flow: updated,
      message:
        messagesOf(updated, "info")[0] ||
        "We emailed you a sign-in code. Enter it below.",
    };
  }

  const problems = [
    ...messagesOf(updated, "error"),
    ...messagesOf(updated, "info"),
  ];

  // Kratos reports an existing session as a flow message rather than an error
  // id, so match on it here too and let the caller adopt that session.
  if (problems.some((text) => /valid session was detected/i.test(text))) {
    return { kind: "alreadySignedIn" };
  }

  return {
    kind: "error",
    flow: updated,
    message: friendlyLoginMessage(problems[0] || GENERIC_ERROR),
  };
}

function friendlyLoginMessage(message: string): string {
  if (/not verified|verified address|verify your email/i.test(message)) {
    return "Confirm the code sent to your HID Global email before signing in with a password.";
  }
  return message;
}

export const signInWithPassword = (
  flow: LoginFlow,
  identifier: string,
  password: string
) => submit(flow, { method: "password", identifier, password });

export const requestSignInCode = (flow: LoginFlow, identifier: string) =>
  submit(flow, { method: "code", identifier });

export const signInWithCode = (
  flow: LoginFlow,
  identifier: string,
  code: string
) => submit(flow, { method: "code", identifier, code });

export const resendSignInCode = (flow: LoginFlow, identifier: string) =>
  submit(flow, { method: "code", identifier, resend: "code" });

const FIDO_NOT_REGISTERED =
  "No security key is registered for this email. Sign in another way, then add one from your account.";

async function postToFlow(
  flow: LoginFlow,
  body: Record<string, unknown>
): Promise<{ res: Response; payload: (Partial<LoginFlow> & { error?: { id?: string; message?: string } }) | null }> {
  const res = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...body, csrf_token: csrfToken(flow) }),
  });
  const payload = res.ok ? null : await res.json().catch(() => null);
  return { res, payload };
}

/**
 * Kratos answers a successful WebAuthn (and some code) sign-in from a JSON
 * client with 422 browser_location_change_required: the session cookie is
 * already set and it just wants the browser to move on.
 */
export function isRedirectAfterSuccess(res: Response, payload: { error?: { id?: string } } | null): boolean {
  return res.status === 422 && payload?.error?.id === "browser_location_change_required";
}

/**
 * Passwordless WebAuthn sign-in is a two-step exchange with Kratos: submitting
 * the identifier answers 400 with a `webauthn_login_trigger` node carrying this
 * account's challenge, and the signed assertion goes back as `webauthn_login`.
 */
export async function signInWithFIDO(
  flow: LoginFlow,
  identifier: string
): Promise<SubmitResult> {
  const first = await postToFlow(flow, { method: "webauthn", identifier });
  if (first.res.ok) return { kind: "success" };

  if (first.payload?.error?.id === "session_already_available") {
    return { kind: "alreadySignedIn" };
  }
  if (first.res.status === 410 || first.payload?.error?.id === "self_service_flow_expired") {
    return { kind: "expired" };
  }

  let challengeFlow: LoginFlow;
  if (isRedirectAfterSuccess(first.res, first.payload)) {
    // For an enrolled account Kratos attaches the challenge to the flow and asks
    // the browser to reload it rather than returning the nodes inline.
    const redirect = (first.payload as { redirect_browser_to?: string } | null)?.redirect_browser_to || "";
    const flowId = new URL(redirect, KRATOS).searchParams.get("flow") || flow.id;
    const refreshed = await fetch(`${KRATOS}/self-service/login/flows?id=${encodeURIComponent(flowId)}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!refreshed.ok) {
      return refreshed.status === 410
        ? { kind: "expired" }
        : { kind: "error", message: `Could not load the security key challenge (HTTP ${refreshed.status}).` };
    }
    challengeFlow = (await refreshed.json()) as LoginFlow;
  } else if (first.payload?.ui) {
    challengeFlow = first.payload as LoginFlow;
  } else {
    return { kind: "error", message: first.payload?.error?.message || GENERIC_ERROR };
  }
  const options = triggerOptions(
    challengeFlow.ui.nodes.find((n) => n.attributes.name === "webauthn_login_trigger")
  );
  if (!options) {
    return {
      kind: "error",
      flow: challengeFlow,
      message: messagesOf(challengeFlow, "error")[0] || FIDO_NOT_REGISTERED,
    };
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey: toRequestOptions(options),
    })) as PublicKeyCredential | null;
  } catch (err) {
    return {
      kind: "error",
      flow: challengeFlow,
      message: isUserCancellation(err)
        ? "Security key prompt was cancelled."
        : `Security key failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (!credential) {
    return { kind: "error", flow: challengeFlow, message: "Security key prompt was cancelled." };
  }

  const second = await postToFlow(challengeFlow, {
    method: "webauthn",
    identifier,
    webauthn_login: serializeAssertion(credential),
  });
  if (second.res.ok || isRedirectAfterSuccess(second.res, second.payload)) return { kind: "success" };
  if (second.res.status === 410 || second.payload?.error?.id === "self_service_flow_expired") {
    return { kind: "expired" };
  }

  const failed = second.payload?.ui ? (second.payload as LoginFlow) : undefined;
  return {
    kind: "error",
    flow: failed,
    message:
      (failed && messagesOf(failed, "error")[0]) ||
      second.payload?.error?.message ||
      "Security key sign-in failed.",
  };
}
